import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/Button";
import { Skeleton } from "@/components/Skeleton";
import { EmptyState } from "@/components/EmptyState";
import { MarkdownView } from "@/components/MarkdownView";
import type { WikiPageRow } from "@shared/types";

type TreePage = {
  id: string;
  parentId: string | null;
  slug: string;
  title: string;
};

/** Replace [[slug]] tokens with in-app markdown links to that wiki page. */
function expandWikiLinks(
  body: string,
  projectId: string,
  bySlug: Map<string, TreePage>,
): string {
  return body.replace(/\[\[([a-z0-9-]+)\]\]/gi, (_m, slug: string) => {
    const page = bySlug.get(slug.toLowerCase());
    const label = page ? page.title : slug;
    return `[${label}](/projects/${projectId}/wiki?page=${slug})`;
  });
}

function WikiView() {
  const { projectId } = Route.useParams();
  const { page: pageParam } = Route.useSearch();
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");

  const listQ = useQuery({
    queryKey: ["wiki", projectId],
    queryFn: () => api.listWikiPages(projectId),
  });
  const pages = (listQ.data?.pages ?? []) as unknown as TreePage[];
  const bySlug = useMemo(
    () => new Map(pages.map((p) => [p.slug.toLowerCase(), p])),
    [pages],
  );

  // Resolve ?page=<slug> (from search / [[links]]) to a selected id.
  useEffect(() => {
    if (pageParam) {
      const p = bySlug.get(pageParam.toLowerCase());
      if (p) setSelectedId(p.id);
    }
  }, [pageParam, bySlug]);

  const selectedQ = useQuery({
    queryKey: ["wiki-page", selectedId],
    queryFn: () => api.getWikiPage(selectedId!),
    enabled: !!selectedId,
  });
  const selected = selectedQ.data?.page as WikiPageRow | undefined;
  const backlinks = selectedQ.data?.backlinks ?? [];

  useEffect(() => {
    if (selected) {
      setTitle(selected.title);
      setBody(selected.body);
      setEditing(false);
    }
  }, [selectedId, selected?.id]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["wiki", projectId] });
    if (selectedId)
      qc.invalidateQueries({ queryKey: ["wiki-page", selectedId] });
  };

  const create = useMutation({
    mutationFn: (parentId: string | null) =>
      api.createWikiPage(projectId, { title: "Untitled", parentId }),
    onSuccess: (r) => {
      invalidate();
      setSelectedId(r.page.id);
    },
  });
  const save = useMutation({
    mutationFn: () =>
      api.updateWikiPage(selectedId!, { title: title.trim(), body }),
    onSuccess: () => {
      invalidate();
      setEditing(false);
    },
  });
  const remove = useMutation({
    mutationFn: () => api.deleteWikiPage(selectedId!),
    onSuccess: () => {
      invalidate();
      setSelectedId(null);
    },
  });

  // Build the tree.
  const roots = pages.filter((p) => !p.parentId);
  const childrenOf = (id: string) => pages.filter((p) => p.parentId === id);

  function renderNode(p: TreePage, depth: number): ReactNode {
    return (
      <li key={p.id}>
        <button
          type="button"
          className={`wiki-tree-item${selectedId === p.id ? " on" : ""}`}
          style={{ paddingLeft: 8 + depth * 14 }}
          onClick={() => setSelectedId(p.id)}
        >
          {p.title}
        </button>
        {childrenOf(p.id).length > 0 && (
          <ul className="wiki-tree">
            {childrenOf(p.id).map((ch) => renderNode(ch, depth + 1))}
          </ul>
        )}
      </li>
    );
  }

  return (
    <div className="proj-shell-content wiki-page">
      <aside className="wiki-sidebar">
        <div className="wiki-side-head">
          <strong className="t-sm">Pages</strong>
          <Button
            variant="primary"
            size="sm"
            onClick={() => create.mutate(null)}
          >
            New
          </Button>
        </div>
        {listQ.isLoading ? (
          <Skeleton count={5} height={26} />
        ) : pages.length === 0 ? (
          <p className="t-sm t-muted" style={{ padding: "6px 4px" }}>
            No pages yet.
          </p>
        ) : (
          <ul className="wiki-tree">{roots.map((r) => renderNode(r, 0))}</ul>
        )}
      </aside>

      <section className="wiki-main">
        {!selectedId ? (
          <EmptyState
            icon="clip"
            title="Select or create a page"
            caption="A hierarchical knowledge base for this project. Link pages with [[slug]]."
          />
        ) : selectedQ.isLoading || !selected ? (
          <Skeleton count={6} height={20} />
        ) : editing ? (
          <div className="wiki-editor">
            <input
              className="input wiki-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              aria-label="Page title"
            />
            <textarea
              className="input wiki-body-input"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              aria-label="Page body (Markdown, [[slug]] to link)"
              rows={20}
            />
            <div className="docs-actions">
              <Button
                variant="primary"
                onClick={() => save.mutate()}
                disabled={save.isPending || !title.trim()}
              >
                {save.isPending ? "Saving…" : "Save"}
              </Button>
              <Button variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="wiki-view">
            <div className="wiki-view-head">
              <h2 className="docs-h">{selected.title}</h2>
              <div className="docs-actions">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => create.mutate(selected.id)}
                >
                  Add subpage
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setEditing(true)}
                >
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (confirm(`Delete "${selected.title}"?`)) remove.mutate();
                  }}
                >
                  Delete
                </Button>
              </div>
            </div>
            <MarkdownView
              source={
                expandWikiLinks(selected.body, projectId, bySlug) ||
                "_Empty page._"
              }
            />
            {backlinks.length > 0 && (
              <div className="wiki-backlinks">
                <strong className="t-sm">Linked from</strong>
                <ul>
                  {backlinks.map((b) => (
                    <li key={b.id}>
                      <button
                        type="button"
                        className="adr-link"
                        onClick={() => setSelectedId(b.id)}
                      >
                        {b.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

export const Route = createFileRoute("/projects/$projectId/wiki")({
  validateSearch: (search: Record<string, unknown>): { page?: string } => ({
    page: typeof search.page === "string" ? search.page : undefined,
  }),
  component: WikiView,
});
